import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PSIFlowSummary, FlowType } from '@/types/psiFlow';

interface FlowListViewProps {
  flows: PSIFlowSummary[];
  isLoading?: boolean;
  selectedFlowId?: string;
  onSelectFlow: (flowId: string) => void;
}

const flowTypeLabels: Record<FlowType, { en: string; icon: string }> = {
  procurement: { en: 'Procurement', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4' },
  sales: { en: 'Sales', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2' },
};

const statusRowColors: Record<string, string> = {
  completed: 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100 dark:hover:bg-green-900/20',
  in_progress: 'bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/10',
  overdue: 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20',
  cancelled: 'bg-gray-50 dark:bg-gray-900/10 hover:bg-gray-100 dark:hover:bg-gray-800',
};

const statusBadgeColors: Record<string, string> = {
  completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const FlowListView: React.FC<FlowListViewProps> = ({
  flows,
  isLoading,
  selectedFlowId,
  onSelectFlow,
}) => {
  const { t } = useTranslation('psi');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="mt-2">{t('flow.noFlows', 'No flows found')}</p>
      </div>
    );
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency || 'TWD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.docNumber', 'Doc #')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.type', 'Type')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.counterparty', 'Counterparty')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.amount', 'Amount')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.stage', 'Current Stage')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.status', 'Status')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
              {t('flow.columns.date', 'Created')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {flows.map((flow) => (
            <tr
              key={flow.flow_id}
              onClick={() => onSelectFlow(flow.flow_id)}
              className={`cursor-pointer transition-colors ${
                selectedFlowId === flow.flow_id
                  ? 'ring-2 ring-indigo-500 ring-inset'
                  : ''
              } ${statusRowColors[flow.status] || statusRowColors.in_progress}`}
            >
              <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap">
                {flow.doc_number || flow.flow_id.slice(0, 8)}
                {flow.is_overdue && (
                  <svg className="inline-block w-4 h-4 ml-1 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  flow.flow_type === 'procurement'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                    : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                }`}>
                  {flowTypeLabels[flow.flow_type]?.en || flow.flow_type}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {flow.counterparty || '-'}
              </td>
              <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {flow.amount > 0 ? formatAmount(flow.amount, flow.currency) : '-'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap capitalize">
                {flow.current_stage?.replace(/_/g, ' ') || '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${
                  statusBadgeColors[flow.status] || statusBadgeColors.in_progress
                }`}>
                  {flow.status?.replace(/_/g, ' ') || 'unknown'}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {flow.created_at ? new Date(flow.created_at).toLocaleDateString() : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FlowListView;
